import axiosInstance from '../axiosInstance';
import type { AxiosResponse } from 'axios';
import type { Task, ModelRef, StepType, TaskStep } from './types';
import { dtoToTaskStep, resolveStepTypeId, type StepDtoBase } from './stepMapping';
import qs from 'qs';

// Backend wire shape for a task step (stepTypeID FK instead of the editor's enum).
interface TaskStepDto extends StepDtoBase {
  taskStepID?: number;
}

// Process-lifetime cache: StepType is static seed data, identical for every user/session.
let stepTypesCache: StepType[] | null = null;

const fromDto = (dto: TaskStepDto): TaskStep => dtoToTaskStep(dto, dto.taskStepID);

const toStepDto = (step: TaskStep, stepTypes: StepType[]): TaskStepDto => ({
  taskStepID: step.taskStepID,
  sequence: step.sequence,
  stepTypeID: resolveStepTypeId(step.type, stepTypes),
  description: step.description,
  isTracked: step.isTracked,
  assayCode: step.assayCode ?? null,
  assayRequired: step.assayRequired ?? false,
});

export default {
  async getTasksForWOCreation(selectedSite: string, selectedCategory?: string): Promise<Task[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/workOrder`, {
        params: {
          selectedSite,
          selectedCategory
        }
      });
      console.log('Response:', response);
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getModelsForTask(eliCategory: string, subcategoryName?: string, query?: string): Promise<ModelRef[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/models`, {
        params: { eliCategory, subcategoryName, query }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching models for task:', error);
      throw error;
    }
  },
  async getTasksForEliCode(eliCode: string): Promise<Task[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/forEliCode`, {
        params: { eliCode }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks for ELI code:', error);
      throw error;
    }
  },
  async getApplicableTasks(pairs: { site: string; category?: string }[]): Promise<Task[]> {
    try {
      const response = await axiosInstance.post(`/api/Task/applicable`, { pairs });
      return response.data;
    } catch (error) {
      console.error('Error fetching applicable tasks:', error);
      throw error;
    }
  },
  async getTasksByFilters(page: number, rows: number, selectedSites: string[], selectedJobTypes: string[], selectedCategories: string[], status: string[], outOfService: boolean | null, searchQuery: string, sortField: string = '', sortOrder: number = 1): Promise<{ data: Task[], totalRecords: number }> {
    try {
      const request = {
        page,
        rows,
        selectedSites,
        selectedJobTypes,
        selectedCategories,
        status,
        outOfService,
        searchQuery,
        sortField,
        sortOrder
      };

      const response = await axiosInstance.post('/api/Task/filtered', request);

      console.log('Response:', response);
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async createTask(task: Task): Promise<AxiosResponse<Task>> {
    try {
      const response = await axiosInstance.post(`/api/Task`, task, { suppressForbiddenRedirect: true });
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteTask(task: Task): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/Task/delete`, task);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getTaskByCode(taskCode: string, includeDeleted = false): Promise<Task> {
    try {
      const response: AxiosResponse<Task> = await axiosInstance.get(`/api/Task/byCode`, {
        params: { taskCode, includeDeleted }
      });
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async taskCodeExists(taskCode: string): Promise<boolean> {
    try {
      const response = await axiosInstance.get(`/api/Task/exists`, {
        params: { taskCode }
      });
      return response.data === true;
    } catch {
      return false;
    }
  },
  async isUsedInPmSchedule(taskId: number): Promise<boolean> {
    try {
      const response = await axiosInstance.get(`/api/Task/usedInPmSchedule`, {
        params: { taskId },
      });
      return response.data === true;
    } catch (error) {
      console.error('Error fetching PM Schedule usage:', error);
      throw error;
    }
  },
  async getSubcategories(eliCategory: string): Promise<string[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/subcategories`, {
        params: { eliCategory }
      });
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getTaskCodesByCategories(eliCategories: string[]): Promise<string[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/codes`, {
        params: { eliCategories },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
        suppressForbiddenRedirect: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching task codes by categories:', error);
      throw error;
    }
  },
  async getEliCodesByTaskId(taskId: number): Promise<string[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/eliCodes`, {
        params: { taskId }
      });
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getNextCode(prefix: string): Promise<string> {
    const response = await axiosInstance.get(`/api/Task/nextCode`, {
      params: { prefix }
    });
    return response.data;
  },
  async getStepTypes(): Promise<StepType[]> {
    if (stepTypesCache) return stepTypesCache;
    try {
      const response = await axiosInstance.get(`/api/Task/stepTypes`);
      stepTypesCache = response.data;
      return response.data;
    } catch (error) {
      console.error('Error fetching step types:', error);
      throw error;
    }
  },
  async getTaskSteps(taskId: number): Promise<TaskStep[]> {
    try {
      const response = await axiosInstance.get(`/api/Task/${taskId}/Steps`);
      return (response.data as TaskStepDto[]).map(fromDto);
    } catch (error) {
      console.error('Error fetching task steps:', error);
      throw error;
    }
  },
  async replaceTaskSteps(taskId: number, steps: TaskStep[]): Promise<TaskStep[]> {
    try {
      const stepTypes = await this.getStepTypes();
      const payload: TaskStepDto[] = steps.map((step) => toStepDto(step, stepTypes));
      const response = await axiosInstance.put(`/api/Task/${taskId}/Steps`, payload);
      return (response.data as TaskStepDto[]).map(fromDto);
    } catch (error) {
      console.error('Error saving task steps:', error);
      throw error;
    }
  },
  async saveTaskWithSteps(task: Task, steps: TaskStep[] | null): Promise<TaskStep[]> {
    try {
      const stepTypes = steps ? await this.getStepTypes() : [];
      const body = { task, steps: steps ? steps.map((step) => toStepDto(step, stepTypes)) : null };
      const response = await axiosInstance.put(`/api/Task/withSteps`, body, { suppressForbiddenRedirect: true });
      return (response.data as TaskStepDto[]).map(fromDto);
    } catch (error) {
      console.error('Error saving task with steps:', error);
      throw error;
    }
  },
  async approveTask(taskId: number): Promise<Task> {
    const response = await axiosInstance.put(`/api/Task/approve`, null, {
      params: { taskId }
    });
    return response.data;
  },
  async exportTasks(request: {
    selectedSites?: string[];
    selectedJobTypes?: string[];
    selectedCategories?: string[];
    status?: string[];
    outOfService?: boolean | null;
    searchQuery?: string;
    sortField?: string;
    sortOrder?: number;
  }): Promise<Blob> {
    const response = await axiosInstance.post('/api/Task/export', request, { responseType: 'blob', timeout: 0 });
    return response.data;
  },
};
